class SetUpsell extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector('[data-set-upsell-add]');
    this.variantId = this.dataset.variantId;
  }

  connectedCallback() {
    if (!this.button || this.dataset.alreadyInCart === 'true') return;
    this.button.addEventListener('click', this.onAdd.bind(this));
  }

  setLoading(state) {
    this.classList.toggle('is-loading', state);
    if (state) {
      this.button.setAttribute('disabled', 'true');
      this.button.classList.add('btn--loading');
    } else {
      this.button.removeAttribute('disabled');
      this.button.classList.remove('btn--loading');
    }
  }

  async onAdd(event) {
    event.preventDefault();
    if (!this.variantId || !window.FoxTheme) return;

    this.setLoading(true);

    try {
      const addBody = JSON.stringify({
        items: [{ id: parseInt(this.variantId, 10), quantity: 1 }],
      });

      const addResponse = await fetch(FoxTheme.routes.cart_add_url, {
        ...FoxTheme.utils.fetchConfig('javascript'),
        body: addBody,
      });

      const addJson = await addResponse.json();

      if (addJson.status) {
        console.error('Set upsell add error:', addJson.description || addJson.message);
        this.setLoading(false);
        return;
      }

      // On the cart page, re-render in place; elsewhere open the cart drawer.
      const onCartPage = !!document.querySelector('main-cart');
      document.dispatchEvent(
        new CustomEvent('cart:refresh', { detail: { open: !onCartPage } })
      );

      const cartJson = await (
        await fetch(FoxTheme.routes.cart_url, { ...FoxTheme.utils.fetchConfig() })
      ).json();
      FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
    } catch (err) {
      console.error('Set upsell add failed:', err);
      this.setLoading(false);
    }
  }
}

if (!customElements.get('set-upsell')) {
  customElements.define('set-upsell', SetUpsell);
}

/* Cart upgrade — Agentcy for INO Beauty (snippets/cart-upgrade.liquid).
   Swaps the absorbed cart lines for the bigger set: /cart/add.js adds the set (with the tracking
   property), then /cart/update.js lowers each absorbed line by one and writes the tracking attribute.
   Adding first means nothing is lost if the second call fails. The shade pickers choose the set
   variant on the card.
   Lives in this file because set-upsell.js is already loaded on every page and the cart drawer's
   re-rendered markup does not run new <script> tags. */
const UPGRADE_ATTR = 'Sepet Yükseltme';

class CartUpgrade extends HTMLElement {
  connectedCallback() {
    this.btn = this.querySelector('[data-upgrade-btn]');
    this.err = this.querySelector('[data-upgrade-error]');
    this.selects = Array.from(this.querySelectorAll('select[data-slot]'));
    try { this.variants = JSON.parse(this.dataset.variants || '[]'); } catch (e) { this.variants = []; }
    /* The theme's <cart-items> treats every bubbling change/input event inside the cart as a quantity
       edit (validates it as a number, resets the field, may call /cart/change). Keep the shade pickers'
       events on the card. */
    this.selects.forEach((s) => {
      s.addEventListener('change', (e) => { e.stopPropagation(); this.pick(); });
      s.addEventListener('input', (e) => e.stopPropagation());
    });
    this.btn?.addEventListener('click', (e) => { e.preventDefault(); this.apply(); });
    if (this.selects.length) this.pick();
  }

  pick() {
    const want = {};
    this.selects.forEach((s) => { want[s.dataset.slot] = s.value; });
    const match = this.variants.find((v) => Object.keys(want).every((k) => v.o && v.o[k] === want[k]));
    if (match) {
      this.dataset.variantId = String(match.id);
      if (this.btn) this.btn.disabled = false;
      if (this.err) this.err.hidden = true;
    } else {
      this.dataset.variantId = '';
      if (this.btn) this.btn.disabled = true;
      if (this.err) {
        this.err.textContent = 'Bu kombinasyon şu an stokta yok, başka bir renk seç.';
        this.err.hidden = false;
      }
    }
  }

  setLoading(state) {
    this.classList.toggle('is-loading', state);
    if (!this.btn) return;
    this.btn.disabled = state;
    this.btn.classList.toggle('btn--loading', state);
  }

  /* POST to the cart API. A 429 (Shopify throttling) was rejected outright, so it is safe to wait and
     resend (twice at most). Errors carry the HTTP status for the on-card code and the console. */
  async post(path, body, tries = 0) {
    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    const res = await fetch(`${root}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const text = await res.text().catch(() => '');
    let json = null;
    try { json = text ? JSON.parse(text) : null; } catch (e) { json = null; }
    if (res.status === 429 && tries < 2) {
      await new Promise((r) => setTimeout(r, 900 * (tries + 1)));
      return this.post(path, body, tries + 1);
    }
    if (!res.ok || !json || json.status) {
      const err = new Error((json && (json.description || json.message)) || `${path} ${res.status}${text ? '' : ' empty'}`);
      err.code = !text ? `${res.status}e` : String(res.status);
      err.path = path;
      err.body = text.slice(0, 300);
      throw err;
    }
    return json;
  }

  showError(err) {
    if (!this.err) return;
    const code = err && err.code ? ` (kod ${err.code})` : '';
    this.err.textContent = `Sepet güncellenemedi, lütfen tekrar dene.${code}`;
    this.err.hidden = false;
    try {
      window.__cartUpgradeLastError = { path: err && err.path, code: err && err.code, message: err && err.message, body: err && err.body, at: new Date().toISOString() };
      sessionStorage.setItem('cartUpgradeLastError', JSON.stringify(window.__cartUpgradeLastError));
    } catch (e) {}
  }

  async apply() {
    const id = Number(this.dataset.variantId);
    if (!id || this.busy) return;
    this.busy = true;
    this.setLoading(true);
    if (this.err) {
      this.err.textContent = 'Sepet güncellenemedi, lütfen tekrar dene.';
      this.err.hidden = true;
    }
    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    let done = false;
    try {
      let updates = {};
      try { updates = JSON.parse(this.dataset.updates || '{}'); } catch (e) { updates = {}; }

      let current = null;
      try { current = await fetch(`${root}cart.js`, { cache: 'no-store' }).then((r) => r.json()); } catch (e) { current = null; }

      /* Order tracking: the set line carries the hidden property _cart_upgrade (follows the item, so
         it is only in the order if the upgraded set is), and the cart attribute "Sepet Yükseltme"
         keeps every step taken (shown under "Additional details" on the order). The set is added
         first (properties can only be set on add), then the absorbed lines are lowered. */
      const label = this.dataset.upgradeLabel || this.dataset.upsellProduct || 'cart-upgrade';
      const prev = (current && current.attributes && current.attributes[UPGRADE_ATTR]) || '';
      const steps = prev ? `${prev} | ${label}` : label;

      /* The cart page does not re-render its upsell rows, so it just reloads after the cart calls. */
      const onCartPage = !!document.querySelector('main-cart');

      /* Ask for the theme's cart sections in the same response: the theme's cartUpdate subscriber
         (<cart-items>) reads cart.sections[sectionId] and throws without it. */
      const sections = [];
      if (!onCartPage) {
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } })
        );
      }
      const updateBody = { updates, attributes: { [UPGRADE_ATTR]: steps.slice(-900) } };
      const uniq = [...new Set(sections)].slice(0, 5);
      if (uniq.length) {
        updateBody.sections = uniq;
        updateBody.sections_url = window.location.pathname;
      }

      let added = null;
      try {
        added = await this.post('cart/add.js', { items: [{ id, quantity: 1, properties: { _cart_upgrade: label } }] });
      } catch (addErr) {
        /* The add may have gone through even if its answer was lost (empty / 5xx). Look before failing. */
        let after = null;
        try { after = await fetch(`${root}cart.js`, { cache: 'no-store' }).then((r) => r.json()); } catch (e) { after = null; }
        const before = (current && current.items) || [];
        const line = after && (after.items || []).find((i) => Number(i.variant_id) === id
          && i.properties && i.properties._cart_upgrade === label
          && !before.some((b) => b.key === i.key && b.quantity >= i.quantity));
        if (!line) throw addErr;
        added = { items: [line] };
      }
      const addedLine = added && Array.isArray(added.items) ? added.items[0] : null;

      /* The keys on the card come from the last render; the cart may have changed since (free-gift app,
         another tab). Re-resolve them against a fresh cart right before lowering the absorbed lines. */
      let cart = null;
      let lastUpdateErr = null;
      for (let attempt = 0; attempt < 2 && !cart; attempt++) {
        /* First try: the cart read at click time (adding a line never changes other lines' keys).
           Retry: a fresh read, in case something else changed the cart in between. */
        let fresh = current;
        if (attempt > 0 || !fresh) {
          try { fresh = await fetch(`${root}cart.js`, { cache: 'no-store' }).then((r) => r.json()); } catch (e) { fresh = current; }
        }
        updateBody.updates = this.resolveUpdates(updates, fresh, addedLine);
        /* Every absorbed line must still be there, or the set would sit next to the pieces. */
        if (Object.keys(updateBody.updates).length < Object.keys(updates).length) {
          console.warn('Cart upgrade: absorbed lines not found in the cart, attempt', attempt + 1);
          if (attempt === 0) await new Promise((r) => setTimeout(r, 500));
          continue;
        }
        try {
          cart = await this.post('cart/update.js', updateBody);
        } catch (e) {
          lastUpdateErr = e;
          console.warn('Cart upgrade: lowering absorbed lines failed, attempt', attempt + 1, e, e && e.body);
          if (attempt === 0) await new Promise((r) => setTimeout(r, 500));
        }
      }
      if (!cart) {
        /* Undo the added set so the cart is exactly as before, then report the failure. */
        if (addedLine && addedLine.key) {
          try { await this.post('cart/change.js', { id: addedLine.key, quantity: Math.max(0, addedLine.quantity - 1) }); } catch (e) {}
        }
        if (lastUpdateErr) throw lastUpdateErr;
        const missing = new Error('absorbed lines not in cart');
        missing.code = 'satir';
        missing.path = 'cart/update.js';
        throw missing;
      }
      done = true;

      if (onCartPage) {
        window.location.reload();
        return;
      }
      this.refreshCart(cart, root);
    } catch (err) {
      console.error('Cart upgrade failed:', err);
      if (done) {
        /* The cart was already updated; only the re-render step failed. Never show an error then. */
        window.location.reload();
        return;
      }
      this.busy = false;
      this.setLoading(false);
      this.showError(err);
    }
  }

  /* Map the card's {lineKey: newQty} onto the current cart: same key if it still exists, otherwise the
     line with the same variant (key prefix). The new quantity is always "current quantity - 1". Gift
     lines and the set line just added are never touched. */
  resolveUpdates(wanted, cart, addedLine) {
    const items = (cart && Array.isArray(cart.items)) ? cart.items : null;
    if (!items) return wanted;
    const out = {};
    const used = new Set();
    Object.keys(wanted).forEach((key) => {
      const usable = (i) => !used.has(i.key)
        && !(addedLine && i.key === addedLine.key)
        && !(i.properties && i.properties._ino_free_gift)
        && i.quantity > 0;
      let line = items.find((i) => i.key === key && usable(i));
      if (!line) {
        const vid = String(key).split(':')[0];
        line = items.find((i) => String(i.variant_id) === vid && usable(i));
      }
      if (line) {
        used.add(line.key);
        out[line.key] = line.quantity - 1;
      }
    });
    return out;
  }

  /* Re-render the drawer after a successful upgrade. Errors thrown by other cartUpdate subscribers
     must not reach apply(): the cart is already updated at this point. */
  async refreshCart(cart, root) {
    let rendered = false;
    try {
      let payload = cart;
      if (!payload || !Array.isArray(payload.items)) payload = await fetch(`${root}cart.js`).then((r) => r.json());
      if (window.FoxTheme && FoxTheme.pubsub && FoxTheme.pubsub.PUB_SUB_EVENTS && payload.sections) {
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: payload, source: 'cart-upgrade' });
        rendered = true;
      }
    } catch (e) {
      console.warn('Cart upgrade: theme re-render failed, falling back to cart:refresh', e);
    }
    if (!rendered) {
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: true } }));
    }
    /* Safety net: if the theme did not re-render this card within 2.5 s, reload. */
    setTimeout(() => { if (this.isConnected && this.busy) window.location.reload(); }, 2500);
  }
}

if (!customElements.get('cart-upgrade')) {
  customElements.define('cart-upgrade', CartUpgrade);
}
